/**
 * Budget: a hard filter on the shelf price, and never part of a score.
 *
 * Integer minor units only, so the comparison is the same whatever the currency's exponent — the conversion from what someone typed happens once, upstream, with the currency-aware money helpers. Both ends are included: a price equal to the minimum or to the maximum is within budget.
 */
import { FrameInputError } from './errors';
import { ReasonCode, type Reason } from './reasons';

export interface BudgetFilter {
  readonly minMinor: number | null;
  readonly maxMinor: number | null;
}

/** The largest amount the catalog accepts for a price, so no budget can ask for more. */
export const MAX_BUDGET_MINOR = 2_000_000_000;

const isMinorAmount = (value: number) =>
  Number.isInteger(value) && value >= 0 && value <= MAX_BUDGET_MINOR;

/** A budget with both ends empty sets no limit at all. */
export const isBudgetActive = (budget: BudgetFilter | null): budget is BudgetFilter =>
  budget !== null && (budget.minMinor !== null || budget.maxMinor !== null);

export function assertValidBudget(budget: BudgetFilter): void {
  const { minMinor, maxMinor } = budget;
  if (minMinor !== null && !isMinorAmount(minMinor)) {
    throw new FrameInputError(
      'The budget minimum must be whole minor units between 0 and 2,000,000,000',
    );
  }
  if (maxMinor !== null && !isMinorAmount(maxMinor)) {
    throw new FrameInputError(
      'The budget maximum must be whole minor units between 0 and 2,000,000,000',
    );
  }
  if (minMinor !== null && maxMinor !== null && minMinor > maxMinor) {
    throw new FrameInputError('The budget minimum is above its maximum');
  }
}

export function isWithinBudget(priceMinor: number, budget: BudgetFilter | null): boolean {
  if (!Number.isInteger(priceMinor) || priceMinor < 0) {
    throw new FrameInputError('A price must be whole minor units ≥ 0');
  }
  if (!isBudgetActive(budget)) return true;
  if (budget.minMinor !== null && priceMinor < budget.minMinor) return false;
  if (budget.maxMinor !== null && priceMinor > budget.maxMinor) return false;
  return true;
}

/** WITHIN_BUDGET when a budget is set and the price is inside it; nothing when no budget is set. */
export function budgetReasons(priceMinor: number, budget: BudgetFilter | null): readonly Reason[] {
  return isBudgetActive(budget) && isWithinBudget(priceMinor, budget)
    ? [{ code: ReasonCode.WITHIN_BUDGET, params: {} }]
    : [];
}
